import React, { useState } from "react";
import useChat from "../hooks/useChat";
import { firebaseAuth, storage } from "../../../context/Firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import { useAuthState } from "react-firebase-hooks/auth";

const Chat = ({ selectedUserId, selectedUserName, selectedConversationId, onBack }) => {
  const [authUser] = useAuthState(firebaseAuth);
  const currentUserId = authUser?.uid;
  const currentUserName = authUser?.displayName || authUser?.email;

  const { messages = [], sendMessage } = useChat(currentUserId, currentUserName, selectedUserId, selectedUserName, selectedConversationId);
  const [newMessage, setNewMessage] = useState("");
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = React.useRef(null);

  // Auto-scroll to bottom when messages change
  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (newMessage.trim() === "") return;
    await sendMessage(newMessage, 'text');
    setNewMessage("");
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const fileRef = ref(storage, `chatImages/${uuidv4()}`);
      await uploadBytes(fileRef, file);
      const imageUrl = await getDownloadURL(fileRef);
      await sendMessage(imageUrl, "image", file);
    } catch (error) {
      console.error("Error uploading image:", error);
    } finally {
      setUploading(false);
    }
  };

  if (!currentUserId) {
    return <div className="flex items-center justify-center h-screen">Please login to chat</div>;
  }

  return (
    <div className="flex flex-col h-screen bg-white shadow-md">
      {/* Header */}
      <div className="flex items-center p-4 text-white bg-blue-600 shadow">
        <h2 className="text-lg font-semibold">Chat with {selectedUserName}</h2>
      </div>

      {/* Messages */}
      <div className="flex-1 p-4 space-y-3 overflow-y-auto">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            No messages yet. Start the conversation!
          </div>
        ) : (
          messages.map((msg) => {
            // Extract message text safely
            let messageText = '';
            if (typeof msg.message === 'string') {
              messageText = msg.message;
            } else if (msg.message && typeof msg.message === 'object') {
              messageText = msg.message.text || '';
            } else if (msg.text) {
              messageText = typeof msg.text === 'string' ? msg.text : msg.text.text || '';
            }
            
            // Ensure it's a string
            messageText = String(messageText || '');
            
            // Debug: Check sender IDs
            const isMyMessage = msg.senderId === currentUserId;
            console.log('Message:', messageText.substring(0, 20), 'SenderId:', msg.senderId, 'CurrentUserId:', currentUserId, 'IsMyMessage:', isMyMessage);
            
            return (
              <div
                key={msg.id || msg.messageId}
                className={`flex ${
                  isMyMessage ? "justify-end" : "justify-start"
                }`}
              >
                {msg.type === "image" ? (
                  <img
                    src={msg.imageUrl || messageText}
                    alt="sent"
                    className="w-48 h-auto rounded-lg shadow"
                  />
                ) : (
                  <p
                    className={`p-3 max-w-xs text-sm rounded-lg shadow-md ${
                      isMyMessage
                        ? "bg-blue-500 text-white"
                        : "bg-gray-200 text-gray-900"
                    }`}
                  >
                    {messageText}
                  </p>
                )}
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSendMessage} className="flex items-center p-4 bg-gray-100 border-t gap-2">
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type a message..."
          className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-blue-500"
        />
        <input
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="hidden"
          id="fileInput"
        />
        <label
          htmlFor="fileInput"
          className="cursor-pointer px-3 py-2 bg-green-500 text-white rounded-full shadow hover:bg-green-600"
        >
          📎
        </label>
        <button
          type="submit"
          disabled={uploading}
          className="px-4 py-2 text-white transition bg-blue-600 rounded-full shadow hover:bg-blue-700 disabled:opacity-50"
        >
          {uploading ? "Sending..." : "Send"}
        </button>
      </form>
    </div>
  );
};

export default Chat;
